import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, BehaviorSubject } from 'rxjs';
import { takeUntil, startWith } from 'rxjs/operators';

import { fileDownloader } from 'app/utils/file-download-helper';
import { Loggable } from 'app/utils/logging/loggable';
import { resettableSubject } from 'app/utils/stream-util';
import CTAItem from 'entity/CTAItem';
import CTAItemType from 'enums/CTAItemType';
import { CTAItemClickedMessage, OutgoingMessageType } from 'messages/websocket.messages';
import Paging from 'rest/Paging';
import SocialController from 'rest/SocialController';

import { actions, selectors } from '../../state/app';
import { SocialFileService } from '../social-file/social-file.service';
import { WebsocketService } from '../websocket.service';

export enum CTAOperationType {
  ADD = 'add',
  REMOVE = 'remove',
  CLEAR = 'clear',
}
interface CTAOperationBase {
  type: CTAOperationType;
}

export interface AddCTA extends CTAOperationBase {
  type: CTAOperationType.ADD;
  cta: CTAItem;
}

export interface RemoveCTA extends CTAOperationBase {
  type: CTAOperationType.REMOVE;
  cta: CTAItem;
}

export interface ClearCTAs extends CTAOperationBase {
  type: CTAOperationType.CLEAR;
}

export type CTAOperation = AddCTA | RemoveCTA | ClearCTAs;

@Injectable({
  providedIn: 'root',
})
export class CTAService extends Loggable {
  private store = inject(Store);
  private websocketService = inject(WebsocketService);
  private socialFileService = inject(SocialFileService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private ctaOperation = resettableSubject(() => new ReplaySubject<CTAOperation>(Infinity));
  private socialId?: string;
  private roomSetId?: string;
  private socialController = new SocialController();

  // Selected item tracking
  private selectedItemId = new BehaviorSubject<string | number | null>(null);

  public get ctaOperation$(): Observable<CTAOperation> {
    return this.ctaOperation.observable;
  }

  // Expose the selected item ID as an observable
  public get selectedItemId$(): Observable<string | number | null> {
    return this.selectedItemId.asObservable();
  }

  public constructor() {
    super();
    this.setDisplayName('CTAService');
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject<boolean>(1);
    this.store
      .select(selectors.getJoinedSocialId)
      .pipe(startWith(''), takeUntil(this.destroyed$))
      .subscribe((socialId: string | undefined) => {
        if (!socialId) {
          this.clearCTAItems();
          this.deselectItem();
          return;
        }

        if (socialId !== this.socialId) {
          this.ctaOperation.reset();
          this.deselectItem();
        }

        this.socialId = socialId;
        this.clearCTAItems();
        this.retrieveCTAItems(socialId);
      });

    this.store
      .select(selectors.getCurrentConnectedRoomSetId)
      .pipe(startWith(''), takeUntil(this.destroyed$))
      .subscribe((roomSetId: string | undefined) => {
        if (!roomSetId) {
          return;
        }
        this.roomSetId = roomSetId;
      });
  }

  private clearCTAItems(): void {
    this.store.dispatch(actions.SET_CTA_ITEMS({ ctaItems: [] }));
    this.ctaOperation.subject.next({ type: CTAOperationType.CLEAR });
    // Assets.reset();
  }

  public retrieveCTAItems(socialId: string, itemsLimit = 100): void {
    this.trace('Fetching social cta items');
    new SocialController()
      .getCTAItems(socialId, new Paging(0, itemsLimit))
      .then((items) => {
        this.store.dispatch(actions.SET_CTA_ITEMS({ ctaItems: items }));

        items.forEach((item: CTAItem) => {
          this.ctaOperation.subject.next({ type: CTAOperationType.ADD, cta: item });
        });
      })
      .catch((err) => {
        this.error('failed to get cta items', err);
      });
  }

  public handleButtonClick(item: CTAItem): void {
    const id = item.CTAItemId;
    const url = item.url;
    if (id) {
      const msg: CTAItemClickedMessage = {
        t: OutgoingMessageType.CTA_CLICKED,
        CTAItemId: id,
      };
      this.websocketService.sendMessage(msg);
    }
    if (item.type === CTAItemType.URL) {
      window.open(url, '_blank');
    } else if (item.type === CTAItemType.DOWNLOAD && url) {
      // Not sure if this validation is needed but I'm keeping for safety
      const filename = url.split('/').pop() || 'download';
      this.downloadFile(url, filename);
    } else if (item.type === CTAItemType.DOWNLOAD && id) {
      this.socialFileService
        .getCTAItemFiles(id)
        .then((files) => {
          if (!files) {
            this.info('Select files to download');
            return;
          }

          const filesIds = files.flatMap((file) => file.socialFileId ?? '');

          this.downloadCTAFiles(filesIds).catch((e) => this.error('Failed download files', e));
        })
        .catch((e) => this.error('Failed to get cta item files', e));
    }
  }

  private downloadFile(url: string, filename: string) {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename || url.split('/').pop() || 'download';
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(this.error);
  }

  public destroy(): void {
    // Assets.reset();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Helper functions
  private async createCTAItem(socialId: string): Promise<void> {
    const item: CTAItem = {};
    this.trace('Creating CTA Item');

    try {
      const cta = await this.socialController.createCTAItem(socialId, item);
      this.info('CTA item', cta);
    } catch (error) {
      this.error('failed to create cta item', error);
      return;
    }
  }

  private async deleteCTAItem(itemId: number): Promise<void> {
    this.trace('Deleting CTA Item', itemId);

    try {
      await this.socialController.deleteCTAItem(itemId);
    } catch (error) {
      this.error('failed to delete cta item', error);
      return;
    }
  }

  public async createCTA(socialId: string, item: CTAItem): Promise<CTAItem> {
    const createdItem = await this.socialController.createCTAItem(socialId, item);
    // Emit an ADD operation to notify subscribers that a new item has been created
    this.ctaOperation.subject.next({ type: CTAOperationType.ADD, cta: createdItem });
    return createdItem;
  }

  public getCTAItems(socialId: string, page?: Paging): Promise<CTAItem[]> {
    return this.socialController.getCTAItems(socialId, page);
  }

  public async deleteCTA(CTAItemId: number): Promise<void> {
    await this.socialController.deleteCTAItem(CTAItemId);
    // Emit a REMOVE operation to notify subscribers that an item has been deleted
    this.ctaOperation.subject.next({
      type: CTAOperationType.REMOVE,
      cta: { CTAItemId },
    });
  }

  public async updateCTA(item: CTAItem): Promise<CTAItem> {
    const updatedItem = await this.socialController.updateCTAItem(item);
    // Emit an ADD operation to notify subscribers that an item has been updated
    this.ctaOperation.subject.next({ type: CTAOperationType.ADD, cta: updatedItem });
    return updatedItem;
  }

  public async duplicateCTA(CTAItemId: number, name: string): Promise<CTAItem> {
    const duplicatedItem = await this.socialController.copyCTAItem(CTAItemId, name);
    // Emit an ADD operation to notify subscribers that a new item has been duplicated
    this.ctaOperation.subject.next({ type: CTAOperationType.ADD, cta: duplicatedItem });
    return duplicatedItem;
  }

  private async downloadCTAFiles(filesIds: string[]): Promise<void> {
    if (this.roomSetId) {
      await this.socialFileService
        .getDownloadURL(this.roomSetId, filesIds)
        .then(async (res) => {
          await fileDownloader(res, this.roomSetId ?? 'file');
        })
        .catch((err) => {
          this.error('Error while downloading the file', err);
        });
    }
  }

  // Selection methods

  /**
   * Select a CTA item by its ID
   * @param itemId The ID of the CTA item to select
   */
  public selectItem(itemId: string | number | null): void {
    if (itemId === this.selectedItemId.getValue()) {
      return; // No change
    }

    this.trace('Selecting CTA item:', itemId);
    this.selectedItemId.next(itemId);
  }

  /**
   * Deselect the currently selected CTA item
   */
  public deselectItem(): void {
    if (this.selectedItemId.getValue() === null) {
      return; // Already deselected
    }

    this.trace('Deselecting CTA item');
    this.selectedItemId.next(null);
  }

  /**
   * Get the currently selected CTA item ID
   */
  public getSelectedItemId(): string | number | null {
    return this.selectedItemId.getValue();
  }
}
